import PropTypes from "prop-types";
import React from "react";
import ItemActionsMenu from "./ItemActionsMenu";

/**
 * Tone rendered as a click-to-select chip / pill (replaces the old Switch toggle).
 * Active state matches the Figma spec: brand border + brand-50 fill + brand text.
 * Custom (user-created) tones get a marker plus optional inline edit/delete
 * affordances (owner-only) when the caller passes the handlers.
 *
 * @param {{ label: string, active: boolean, onClick: () => void,
 *           isCustom?: boolean, disabled?: boolean,
 *           onEdit?: () => void, onDelete?: () => void }} props
 */
export default function ToneChip({
  label,
  active,
  onClick,
  isCustom = false,
  disabled = false,
  onEdit = null,
  onDelete = null,
}) {
  const handleActivate = () => {
    if (!disabled) onClick();
  };

  // Rendered as a role="button" div (not a <button>) so the custom-item
  // actions menu — itself a button — can nest without invalid button-in-button
  // markup.
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={active}
      aria-disabled={disabled}
      onClick={handleActivate}
      onKeyDown={e => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-solid text-sm transition-colors ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      } ${
        active
          ? "border-[#4096FF] bg-[#E6F4FF] text-[#1677FF]"
          : "border-[#D9D9D9] text-grey-700 bg-white"
      }`}
    >
      {label}
      {isCustom && onEdit && onDelete && <ItemActionsMenu onEdit={onEdit} onDelete={onDelete} />}
    </div>
  );
}

ToneChip.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isCustom: PropTypes.bool,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

ToneChip.defaultProps = {
  isCustom: false,
  disabled: false,
  onEdit: null,
  onDelete: null,
};
